// RV9 · the designer lane. Per-step writes, not whole-record saves.
//
// Two headers make incremental save safe rather than merely possible, and both are the
// client's job to send:
//
//   If-Match         the version the client is holding. A stale one comes back 409 WITH the
//                    current tree, so we rebase instead of re-fetching blindly.
//   Idempotency-Key  a retry on a flaky link must not insert the step twice. The author would
//                    see one and the record would hold two, and the invisible one is the one
//                    that reaches the operator.
import Axios from "axios";

const BASE = "/bp/designer";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("Token")}` });

/** A key per attempt, reused across retries of that same attempt. */
export const newKey = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export async function write(method, url, body, { version, key } = {}) {
  const headers = { ...auth(), "Content-Type": "application/json" };
  if (version != null) headers["If-Match"] = String(version);
  if (key) headers["Idempotency-Key"] = key;
  try {
    const res = await Axios({ method, url, data: body ?? {}, headers });
    return { ok: true, ...res.data };
  } catch (error) {
    const res = error?.response;
    if (res?.status === 409) {
      // The refusal carries the current state so the caller can rebase. Not an error to
      // show as a failure — someone else got there first, which is ordinary.
      return { ok: false, conflict: true, ...res.data };
    }
    return {
      ok: false,
      conflict: false,
      message: res?.data?.message || "That change could not be applied.",
    };
  }
}

export const listRecipes = async () => {
  const res = await Axios.get(`${BASE}/recipes/`, { headers: auth() });
  return res.data?.data || [];
};

// The vocabulary — step types with label, group, code, hint and family; signature meanings;
// presentation limits — served by the backend, which owns it. One fetch per page load.
let _vocab = null;
export const getVocabulary = async () => {
  if (_vocab) return _vocab;
  const res = await Axios.get(`${BASE}/vocabulary/`, { headers: auth() });
  _vocab = res.data;
  return _vocab;
};

/** Submit the whole record to its approval chain (the generic engine; the server gates on readiness). */
export const submitForReview = async (recipeId) => {
  const res = await Axios.post(`/bp/approval/submit/`, { object_type: "Recipe", object_id: Number(recipeId) }, { headers: auth() });
  return res.data;
};

// Inline authoring of what a step works with: checklist items on the shared master list (the
// legacy master endpoints, unchanged), a new list, a new parameter on the parameter master.
const bpAuth = () => ({ headers: auth() });
export const createChecklistItem = async (name) => {
  const res = await Axios.post(`/bp/checklist-item/create/`, { name }, bpAuth()); return res.data?.data || res.data;
};
export const setChecklistItems = async (fieldId, ids) => {
  const res = await Axios.put(`/bp/checklist-field/update/${fieldId}/`, { checklist_item_ids: ids }, bpAuth()); return res.data;
};
export const createChecklist = async (name, ids = []) => {
  const res = await Axios.post(`/bp/checklist-field/create/`, { name, checklist_item_ids: ids }, bpAuth()); return res.data?.data || res.data;
};
export const listControlCategories = () => listOf("control-categories");
export const createParameter = async (body) => {
  const res = await Axios.post(`/bp/add_process_control_parameter`, body, bpAuth()); return res.data;
};

// RV9m · what a step collects: the parameter master, the label master, units; the typed
// fields the operator fills on a step.
const listOf = async (path) => { const res = await Axios.get(`${BASE}/${path}/`, { headers: auth() }); return res.data?.data || []; };
export const listParameters = () => listOf("parameters");
/** 2026-10-30 · the record plant's machine tags, the capture words and the plant's defaults (value source, check rows). */
const _capture = {};
export const getCaptureOptions = (recipeId) => {
  if (!_capture[recipeId]) {
    _capture[recipeId] = Axios.get(`${BASE}/recipes/${recipeId}/capture/`, { headers: auth() })
      .then((r) => r.data)
      .catch(() => { delete _capture[recipeId]; return null; });
  }
  return _capture[recipeId];
};
export const listLabels = () => listOf("labels");
/** Add a label to the label master from the step's field picker — an existing name comes back as it is. */
export const createLabel = async (name) => {
  const res = await Axios.post(`${BASE}/labels/`, { name }, { headers: auth() }); return res.data;
};
export const listUnits = () => listOf("units");
export const addField = (recipeId, code, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/steps/${code}/fields/`, body, ctx);
export const editField = (recipeId, code, fieldId, body, ctx) =>
  write("patch", `${BASE}/recipes/${recipeId}/steps/${code}/fields/${fieldId}/`, body, ctx);
export const deleteField = (recipeId, code, fieldId, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/steps/${code}/fields/${fieldId}/delete/`, {}, ctx);

// The checklist library a step binds to — picked, never typed.
/** The clearance profiles a CLEARANCE step binds to — what the clearance verifies. */
export const listClearanceProfiles = async () => {
  const res = await Axios.get(`${BASE}/clearance-profiles/`, { headers: auth() });
  return res.data?.data || [];
};

export const listChecklists = async () => {
  const res = await Axios.get(`${BASE}/checklists/`, { headers: auth() });
  return res.data?.data || [];
};

// The rules library — every rule on a sub-operation template. A step binds one; it never
// types a value of its own.
export const listRules = async () => {
  const res = await Axios.get(`${BASE}/rules/`, { headers: auth() });
  return res.data?.data || [];
};
export const bindRule = (recipeId, code, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/steps/${encodeURIComponent(code)}/bindings/`, body, ctx);
export const tightenRule = (recipeId, code, bindingId, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/steps/${encodeURIComponent(code)}/bindings/${bindingId}/tighten/`, body, ctx);
export const unbindRule = (recipeId, code, bindingId, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/steps/${encodeURIComponent(code)}/bindings/${bindingId}/unbind/`, {}, ctx);

// The AQL of a Sample step of a visual-inspection operation: level, defect groups with their AQLs, the tray rule.
export const readAqlSetup = async (recipeId, code) => {
  const res = await Axios.get(`${BASE}/recipes/${recipeId}/steps/${encodeURIComponent(code)}/aql-setup/`, { headers: auth() });
  return res.data?.setup || null;
};
export const saveAqlSetup = (recipeId, code, setup, ctx) =>
  write("put", `${BASE}/recipes/${recipeId}/steps/${encodeURIComponent(code)}/aql-setup/save/`, { setup }, ctx);

export const listRoles = async () => {
  const res = await Axios.get(`${BASE}/roles/`, { headers: auth() });
  return res.data?.data || [];
};

export const getProposedOrder = async (operationId) => {
  const res = await Axios.get(`${BASE}/operations/${operationId}/order/`, { headers: auth() });
  return res.data?.data;
};

export const placeRows = (operationId, body, ctx) =>
  write("post", `${BASE}/operations/${operationId}/place/`, body, ctx);

export const getRecipe = async (recipeId) => {
  const res = await Axios.get(`${BASE}/recipes/${recipeId}/`, { headers: auth() });
  return res.data;
};

/** The record's life in order — created, processes added, sent, approved / not approved,
 *  re-sent, finalised: `{recipe, events: [{at, tone, title, actor, role, process, operation, detail, comment}]}`. */
export const getRecordHistory = async (recipeId) => {
  const res = await Axios.get(`${BASE}/recipes/${recipeId}/history/`, { headers: auth() });
  return res.data?.data;
};

export const getFindings = async (recipeId) => {
  const res = await Axios.get(`${BASE}/recipes/${recipeId}/findings/`, { headers: auth() });
  return res.data;
};

// A step is addressed within its record: codes are unique per record, not across the
// table, so `MCH-02` on two records is two steps (D11 §3).
export const getImpact = async (recipeId, code) => {
  const res = await Axios.get(`${BASE}/recipes/${recipeId}/steps/${encodeURIComponent(code)}/impact/`, {
    headers: auth(),
  });
  return res.data;
};

export const insertStep = (recipeId, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/steps/`, body, ctx);

/** 2026-10-29 (owner) · a copy of step `code` at {operation_id, rank?, parent?} — the picker's "Copy step". */
export const copyStep = (recipeId, code, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/steps/${encodeURIComponent(code)}/copy/`, body, ctx);

/** What an operation waits for: {operation_ids, reason?} — a reason when one is taken away. */
export const setDependsOn = (recipeId, operationId, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/operations/${operationId}/depends-on/`, body, ctx);

/** Bring the bill of materials from the MFR: {mfr_id?}. No process or operation is written. */
export const bomFromMfr = (recipeId, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/bom/from-mfr/`, body, ctx);

/** A DECISION with its LANEs, in one write: {operation_id, parent?, rank?, title, lanes}. */
export const insertDecision = (recipeId, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/decisions/`, body, ctx);

/** A multipart write — a file on a step. Same headers and refusal handling as `write`. */
export async function writeForm(url, form, { version, key } = {}) {
  const headers = { ...auth() };
  if (version != null) headers["If-Match"] = String(version);
  if (key) headers["Idempotency-Key"] = key;
  try {
    const res = await Axios.post(url, form, { headers });
    return { ok: true, ...res.data };
  } catch (error) {
    const res = error?.response;
    if (res?.status === 409) return { ok: false, conflict: true, ...res.data };
    return { ok: false, conflict: false, message: res?.data?.message || "That change could not be applied." };
  }
}

/** Images and documents on a step; procedures linked from the SOP library. */
export const uploadAttachment = (recipeId, code, file, { kind, caption }, ctx) => {
  const form = new FormData();
  form.append("file", file);
  form.append("kind", kind || "IMAGE");
  form.append("caption", caption || "");
  return writeForm(`${BASE}/recipes/${recipeId}/steps/${encodeURIComponent(code)}/attachments/`, form, ctx);
};
export const deleteAttachment = (recipeId, code, attachmentId, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/steps/${encodeURIComponent(code)}/attachments/${attachmentId}/delete/`, {}, ctx);
export const linkSop = (recipeId, code, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/steps/${encodeURIComponent(code)}/sops/`, body, ctx);
export const unlinkSop = (recipeId, code, linkId, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/steps/${encodeURIComponent(code)}/sops/${linkId}/unlink/`, body, ctx);

/** F5: what updating an operation to its type's current version would do, and doing it. */
export const typeUpdatePreview = async (recipeId, operationId) => {
  const res = await Axios.get(`${BASE}/recipes/${recipeId}/operations/${operationId}/update-from-type/`, { headers: auth() });
  return res.data;
};
export const updateFromType = (recipeId, operationId, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/operations/${operationId}/update-from-type/`, body, ctx);

/** The cleanroom grade an operation runs in: {grade} from the facility vocabulary. */
export const setArea = (recipeId, operationId, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/operations/${operationId}/area/`, body, ctx);

/**
 * The dispensing lane on an operation: {roles, area_ids}.
 *
 * `roles` are MbomLine roles, so how many lanes a record has is configuration rather than a
 * shape in code. `area_ids` is the PERMITTED set — the floor picks one of them at execution and
 * that choice is an act against the batch, never an edit to the record (spec/21 section 5).
 */
export const setDispensing = (recipeId, operationId, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/operations/${operationId}/dispensing/`, body, ctx);

/** Areas and the booths inside them, for the lane pickers. */
export const listAreas = async () => {
  const res = await Axios.get(`${BASE}/areas/`, { headers: auth() });
  return res.data?.areas || [];
};

/** The handover acknowledgement on an operation: {required, text}. */
/** A new operation of a type on a process: `{process_id, operation_type_id, after?, name?}`.
 *  The answer carries `created_operation`, the new id. */
export const addOperation = (recipeId, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/operations/`, body, ctx);

/** The operation's own facts: `{name?, department_id?, code?, instruction?}`. */
export const editOperation = (recipeId, operationId, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/operations/${operationId}/details/`, body, ctx);

/** The department master — who performs an operation. */
let _departments = null;
export const listDepartments = async () => {
  if (_departments) return _departments;
  const res = await Axios.get("/bp/departdata", { headers: auth() });
  _departments = (res.data?.departdata || []).map((d) => ({ value: d.id, label: d.department_name || d.name }));
  return _departments;
};

/** FR-07 · which step of a filling operation makes its lots, and which field means what: `{step, fields}`.
 *  An empty `step` switches it off. */
export const setReconciliation = (recipeId, operationId, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/operations/${operationId}/reconciliation/`, body, ctx);
export const setLotStep = (recipeId, operationId, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/operations/${operationId}/lot-step/`, body, ctx);

export const setHandover = (recipeId, operationId, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/operations/${operationId}/handover/`, body, ctx);

/** How many stations run an operation at once. 0 or 1 clears it. */
export const promoteOperation = (recipeId, operationId, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/operations/${operationId}/promote/`, body, ctx);
export const setConcurrency = (recipeId, operationId, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/operations/${operationId}/concurrency/`, body, ctx);

export const editStep = (recipeId, code, body, ctx) =>
  write("patch", `${BASE}/recipes/${recipeId}/steps/${encodeURIComponent(code)}/`, body, ctx);

export const moveStep = (recipeId, code, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/steps/${encodeURIComponent(code)}/move/`, body, ctx);

export const deleteStep = (recipeId, code, body, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/steps/${encodeURIComponent(code)}/delete/`, body, ctx);

export const wrapInLoop = (operationId, body, ctx) =>
  write("post", `${BASE}/operations/${operationId}/loop/`, body, ctx);

// RV9p · the editing lock. Claimed when a record opens for editing, kept alive by a
// heartbeat, released on the way out; taken over only once the heartbeat has lapsed.
const lockUrl = (recipeId, tail = "") => `${BASE}/recipes/${recipeId}/lock/${tail}`;
const lockResult = (res) => res.data;
const lockError = (err) => {
  // A 423 carries the holder — that is an answer, not a failure to surface as a toast.
  if (err?.response?.status === 423 || err?.response?.status === 409) return err.response.data;
  throw err;
};
export const getLock = (recipeId) => Axios.get(lockUrl(recipeId), { headers: auth() }).then(lockResult);
export const claimLock = (recipeId) => Axios.post(lockUrl(recipeId), {}, { headers: auth() }).then(lockResult, lockError);
export const heartbeatLock = (recipeId) => Axios.post(lockUrl(recipeId, "heartbeat/"), {}, { headers: auth() }).then(lockResult, lockError);
export const takeOverLock = (recipeId, reason) => Axios.post(lockUrl(recipeId, "take-over/"), { reason }, { headers: auth() }).then(lockResult, lockError);
/** Release, also on the way out of the page: `keepalive` lets the request outlive the tab. */
export const releaseLock = (recipeId) => {
  try {
    return fetch(lockUrl(recipeId, "release/"), { method: "POST", keepalive: true,
                                                   headers: { ...auth(), "Content-Type": "application/json" }, body: "{}" })
      .then((r) => r.json()).catch(() => null);
  } catch {
    return Promise.resolve(null);
  }
};

// RV9q · a product's master records, each with its versions and the one in force.
export const listFamilies = async (params = {}) => {
  const res = await Axios.get(`${BASE}/families/`, { headers: auth(), params });
  return res.data?.data || [];
};

// RV9u · a timed rule updated from the plant policy it draws from — one rule, or every rule on the record.
export const refreshRuleFromPolicy = (recipeId, code, bindingId, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/steps/${encodeURIComponent(code)}/bindings/${bindingId}/refresh-from-policy/`, {}, ctx);
export const refreshRulesFromPolicy = (recipeId, ctx) =>
  write("post", `${BASE}/recipes/${recipeId}/rules/refresh-from-policy/`, {}, ctx);
